import { useEffect, useMemo, useRef, useState } from 'react';
import type { Side, HandicapConfig } from '@xiangqi/protocol';
import type { GameStateJson, PieceKindId, XiangqiCore, HandicapConfigJson } from '@xiangqi/game-core';
import { getPremoveDestinations, isRoyalPiece, shouldCancelPremoveOnTap } from '../game/premove.ts';

const CELL = 90;
const MARGIN = 55;
const W = MARGIN * 2 + CELL * 8;
const H = MARGIN * 2 + CELL * 9;

export const RED_CHARS: Record<PieceKindId, string> = {
  general: '帥',
  advisor: '仕',
  elephant: '相',
  horse: '傌',
  chariot: '俥',
  cannon: '炮',
  soldier: '兵',
};
export const BLACK_CHARS: Record<PieceKindId, string> = {
  general: '將',
  advisor: '士',
  elephant: '象',
  horse: '馬',
  chariot: '車',
  cannon: '砲',
  soldier: '卒',
};

export function getStoredPieceSkin(): 'classic' | 'kychien' {
  if (typeof window === 'undefined') return 'classic';
  return (localStorage.getItem('xq_piece_skin') as 'classic' | 'kychien') || 'classic';
}

export function getPieceAsset(
  side: Side,
  kind?: PieceKindId | null,
  isRoyal: boolean = false,
  isCovered: boolean = false,
  skin: 'classic' | 'kychien' = getStoredPieceSkin(),
): string {
  if (skin === 'kychien') {
    const base = '/assets/pieces_kychien';
    if (isCovered || !kind) {
      return side === 'black' ? `${base}/covered_black.png` : `${base}/covered_red.png`;
    }
    if (isRoyal && (kind === 'advisor' || kind === 'elephant')) {
      return `${base}/${side}_royal_${kind}.png`;
    }
    return `${base}/${side}_${kind}.png`;
  }

  // Gỗ Mộc Cổ Điển ('classic') - đồng bộ chất liệu gỗ mộc cho toàn bộ bàn cờ
  const base = '/assets/pieces';
  if (isCovered || !kind) {
    return `${base}/covered.png`;
  }
  if (isRoyal && (kind === 'advisor' || kind === 'elephant')) {
    return `${base}/${side}_royal_${kind}.png`;
  }
  return `${base}/${side}_${kind}.png`;
}

export const PIECE_ASSETS: Record<Side, Record<PieceKindId, string>> = {
  red: {
    general: '/assets/pieces/red_general.png',
    advisor: '/assets/pieces/red_advisor.png',
    elephant: '/assets/pieces/red_elephant.png',
    horse: '/assets/pieces/red_horse.png',
    chariot: '/assets/pieces/red_chariot.png',
    cannon: '/assets/pieces/red_cannon.png',
    soldier: '/assets/pieces/red_soldier.png',
  },
  black: {
    general: '/assets/pieces/black_general.png',
    advisor: '/assets/pieces/black_advisor.png',
    elephant: '/assets/pieces/black_elephant.png',
    horse: '/assets/pieces/black_horse.png',
    chariot: '/assets/pieces/black_chariot.png',
    cannon: '/assets/pieces/black_cannon.png',
    soldier: '/assets/pieces/black_soldier.png',
  },
};
export const ROYAL_PIECE_ASSETS: Record<Side, Partial<Record<PieceKindId, string>>> = {
  red: {
    advisor: '/assets/pieces/red_royal_advisor.png',
    elephant: '/assets/pieces/red_royal_elephant.png',
  },
  black: {
    advisor: '/assets/pieces/black_royal_advisor.png',
    elephant: '/assets/pieces/black_royal_elephant.png',
  },
};
export const COVERED_ASSET = '/assets/pieces/covered.png';

function getOpeningMoves(ruleset?: string, h?: HandicapConfig | HandicapConfigJson | null): number {
  if (!h || !ruleset) return 0;
  if (ruleset === 'blind') return 0;
  if (ruleset === 'standard') {
    if ('type' in h && h.type !== 'moves') return 0;
    if ('opening_moves' in h && typeof h.opening_moves === 'number') return h.opening_moves;
    if ('current_moves' in h && typeof h.current_moves === 'number') return h.current_moves;
    if ('moves_count' in h && typeof h.moves_count === 'number') return h.moves_count;
    return 0;
  }
  if (ruleset === 'blind_si_chua' || ruleset === 'blind_tuong_vuong') {
    if ('type' in h && h.type !== 'asym_royal') return 0;
    if ('opening_moves' in h && typeof h.opening_moves === 'number') return h.opening_moves;
    if ('current_moves' in h && typeof h.current_moves === 'number') return h.current_moves;
    return ruleset === 'blind_si_chua' ? 7 : 6;
  }
  return 0;
}

interface BoardProps {
  state: GameStateJson;
  core: XiangqiCore | null;
  yourSide: Side | null;
  flipped?: boolean;
  interactive: boolean;
  lastMove: { from: number; to: number } | null;
  pendingMove?: { from: number; to: number } | null;
  premove?: { from: number; to: number } | null;
  hintMove?: { from: number; to: number } | null;
  handicap?: HandicapConfig | HandicapConfigJson | null;
  inCheck?: boolean;
  onMove?(from: number, to: number): void;
  onCancelPremove?(): void;
}

export function Board({
  state,
  core,
  yourSide,
  flipped = false,
  interactive,
  lastMove,
  pendingMove,
  premove,
  hintMove,
  handicap,
  inCheck,
  onMove,
  onCancelPremove,
}: BoardProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const rightClickStartRef = useRef<number | null>(null);
  const [tacticalArrows, setTacticalArrows] = useState<Array<{ from: number; to: number }>>([]);
  const [highlightedSquares, setHighlightedSquares] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<number | null>(null);
  const [justRevealedIds, setJustRevealedIds] = useState<Set<number>>(new Set());
  const [currentSkin, setCurrentSkin] = useState<'classic' | 'kychien'>(getStoredPieceSkin);
  const prevCoveredRef = useRef<Set<number>>(new Set());
  const isFirstMountRef = useRef(true);
  const flip = (yourSide === 'black') !== flipped;
  const activeHandicap = state.ruleset === 'blind' ? null : (state.handicap ?? handicap);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'c' || e.key === 'C') {
        setTacticalArrows([]);
        setHighlightedSquares(new Set());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const getSquareFromClient = (clientX: number, clientY: number): number | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
    const fileFrac = (svgP.x - MARGIN) / CELL;
    const rankFrac = (svgP.y - MARGIN) / CELL;
    const f = Math.round(fileFrac);
    const r = Math.round(rankFrac);
    if (f < 0 || f > 8 || r < 0 || r > 9) return null;
    const distSq = (fileFrac - f) ** 2 + (rankFrac - r) ** 2;
    if (distSq > 0.45) return null;
    const actualF = flip ? 8 - f : f;
    const actualR = flip ? r : 9 - r;
    return actualR * 9 + actualF;
  };

  useEffect(() => {
    const onSkinChanged = () => setCurrentSkin(getStoredPieceSkin());
    window.addEventListener('xq_skin_changed', onSkinChanged);
    return () => window.removeEventListener('xq_skin_changed', onSkinChanged);
  }, []);

  useEffect(() => {
    if (state.ruleset === 'standard') return;

    const currentCovered = new Set<number>();
    for (const p of state.board.squares) {
      if (p && p.covered) {
        currentCovered.add(p.id);
      }
    }

    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      prevCoveredRef.current = currentCovered;
      return;
    }

    const newlyRevealed = new Set<number>();
    for (const p of state.board.squares) {
      if (!p) continue;
      if (prevCoveredRef.current.has(p.id) && !p.covered) {
        newlyRevealed.add(p.id);
      }
    }
    prevCoveredRef.current = currentCovered;

    if (newlyRevealed.size > 0) {
      setJustRevealedIds(newlyRevealed);
      const timer = setTimeout(() => setJustRevealedIds(new Set()), 250);
      return () => clearTimeout(timer);
    }
  }, [state]);

  const pos = (sq: number): { x: number; y: number } => {
    const f = sq % 9;
    const r = Math.floor(sq / 9);
    const x = MARGIN + (flip ? 8 - f : f) * CELL;
    const y = MARGIN + (flip ? r : 9 - r) * CELL;
    return { x, y };
  };

  // Render the board with the optimistic move applied visually.
  const squares = useMemo(() => {
    const list = state.board.squares.slice();
    if (pendingMove) {
      const piece = list[pendingMove.from];
      if (piece) {
        list[pendingMove.from] = null;
        list[pendingMove.to] = piece;
      }
    }
    return list;
  }, [state, pendingMove]);

  const isPremoveTurn = !!yourSide && state.turn !== yourSide;
  const legal = useMemo(() => {
    if (!core || !interactive || pendingMove || isPremoveTurn) return { moves: [], in_check: false };
    try {
      return core.legalMoves(state);
    } catch {
      return { moves: [], in_check: false };
    }
  }, [core, state, interactive, pendingMove, isPremoveTurn]);

  const targets = useMemo(() => {
    if (!interactive || pendingMove || selected === null) return new Set<number>();
    if (isPremoveTurn) {
      return new Set(getPremoveDestinations(selected, squares, state.ruleset, activeHandicap));
    }
    return new Set(legal.moves.filter((m) => m.from === selected).map((m) => m.to));
  }, [interactive, pendingMove, selected, isPremoveTurn, squares, state.ruleset, activeHandicap, legal]);

  const checkedGeneral = useMemo(() => {
    if (!core || !interactive) return null;
    try {
      const isCheck =
        inCheck !== undefined
          ? inCheck
          : isPremoveTurn
            ? core.legalMoves(state).in_check
            : legal.in_check;
      if (!isCheck) return null;
      const idx = squares.findIndex(
        (p) => p != null && p.side === state.turn && !p.covered && p.kind === 'general',
      );
      return idx >= 0 ? idx : null;
    } catch {
      return null;
    }
  }, [core, interactive, inCheck, isPremoveTurn, state, legal, squares]);

  const lastTapRef = useRef<{ time: number } | null>(null);

  const checkTap = (isMove: boolean): { isDoubleTap: boolean; cancelPremove: boolean } => {
    const now = Date.now();
    const prevTime = lastTapRef.current?.time ?? null;
    const result = shouldCancelPremoveOnTap(prevTime, now, isMove);
    if (result.isDoubleTap) {
      lastTapRef.current = null;
    } else {
      lastTapRef.current = { time: now };
    }
    return result;
  };

  const handleBgClick = (): void => {
    if (tacticalArrows.length > 0) setTacticalArrows([]);
    if (highlightedSquares.size > 0) setHighlightedSquares(new Set());
    if (!interactive || pendingMove) return;
    const { cancelPremove } = checkTap(false);
    if (cancelPremove && premove) {
      onCancelPremove?.();
    }
    setSelected(null);
  };

  const handleTap = (sq: number): void => {
    if (tacticalArrows.length > 0) setTacticalArrows([]);
    if (highlightedSquares.size > 0) setHighlightedSquares(new Set());
    if (!interactive || pendingMove) return;
    const piece = squares[sq];
    const isMove = selected !== null && sq !== selected && targets.has(sq);
    const { isDoubleTap, cancelPremove } = checkTap(isMove);

    if (isMove) {
      setSelected(null);
      onMove?.(selected, sq);
      return;
    }

    if (cancelPremove && premove) {
      onCancelPremove?.();
      setSelected(null);
      return;
    }

    if (piece && piece.side === yourSide) {
      const openingMoves = getOpeningMoves(state.ruleset, activeHandicap);
      const isHandicapPhase = openingMoves > 0 && state.ply < openingMoves;
      if (isHandicapPhase && state.handicap_moved_pieces?.includes(piece.id)) {
        return;
      }
      if (sq === selected) {
        setSelected(null);
        return;
      }
      setSelected(sq);
      return;
    }

    if (isDoubleTap) {
      setSelected(null);
      return;
    }

    setSelected(null);
  };

  return (
    <svg
      ref={svgRef}
      className="board"
      viewBox={`0 0 ${W} ${H}`}
      data-testid="board"
      role="grid"
      aria-label="Bàn cờ tướng"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleBgClick();
        }
      }}
      onMouseDown={(e) => {
        if (e.button === 2) {
          rightClickStartRef.current = getSquareFromClient(e.clientX, e.clientY);
        }
      }}
      onMouseUp={(e) => {
        if (e.button === 2) {
          const sqStart = rightClickStartRef.current;
          rightClickStartRef.current = null;
          const sqEnd = getSquareFromClient(e.clientX, e.clientY);
          if (sqStart !== null && sqEnd !== null) {
            if (sqStart === sqEnd) {
              setHighlightedSquares((prev) => {
                const next = new Set(prev);
                if (next.has(sqStart)) next.delete(sqStart);
                else next.add(sqStart);
                return next;
              });
            } else {
              setTacticalArrows((prev) => {
                const exists = prev.findIndex((a) => a.from === sqStart && a.to === sqEnd);
                if (exists >= 0) return prev.filter((_, i) => i !== exists);
                return [...prev, { from: sqStart, to: sqEnd }];
              });
            }
          }
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        setSelected(null);
        if (premove) onCancelPremove?.();
      }}
    >
      <defs>
        <pattern id="wood-grain-pattern" patternUnits="userSpaceOnUse" width="400" height="400">
          <image href={currentSkin === 'kychien' ? "/assets/board_kychien/wood_grain.png" : "/assets/board/wood_grain.png"} width="400" height="400" />
        </pattern>
        <marker id="arrow-hint" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#2ed573" />
        </marker>
        <marker id="arrow-last-move" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 1 2 L 8 5 L 1 8 z" fill="#e5a93c" />
        </marker>
        <marker id="arrow-tactical" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 1 L 9 5 L 0 9 z" fill="#22c55e" />
        </marker>
      </defs>

      {/* Board wooden background texture */}
      <rect
        x="0"
        y="0"
        width={W}
        height={H}
        rx="12"
        fill="url(#wood-grain-pattern)"
        className="board-bg"
        onClick={handleBgClick}
      />
      <rect x="0" y="0" width={W} height={H} rx="12" fill="rgba(216, 176, 120, 0.35)" />
      <rect x="4" y="4" width={W - 8} height={H - 8} rx="10" fill="none" stroke="#5a3d1e" strokeWidth="3" opacity="0.8" />

      <BoardLines />
      {lastMove && (() => {
        const fromPos = pos(lastMove.from);
        const toPos = pos(lastMove.to);

        return (
          <g className="mark-last-under-group" pointerEvents="none">
            {/* Origin glowing reticle (like modern Xiangqi apps) */}
            <circle cx={fromPos.x} cy={fromPos.y} r="15" fill="rgba(255, 251, 235, 0.35)" className="mark-last-glow" />
            <circle cx={fromPos.x} cy={fromPos.y} r="10.5" stroke="rgba(255, 251, 235, 0.95)" strokeWidth="1.8" fill="none" className="mark-last-ring" />
            <circle cx={fromPos.x} cy={fromPos.y} r="2.8" fill="rgba(255, 251, 235, 0.95)" className="mark-last-dot" />
            <line x1={fromPos.x - 16.5} y1={fromPos.y} x2={fromPos.x - 10.5} y2={fromPos.y} stroke="rgba(255, 251, 235, 0.9)" strokeWidth="1.6" strokeLinecap="round" className="mark-last-tick" />
            <line x1={fromPos.x + 10.5} y1={fromPos.y} x2={fromPos.x + 16.5} y2={fromPos.y} stroke="rgba(255, 251, 235, 0.9)" strokeWidth="1.6" strokeLinecap="round" className="mark-last-tick" />
            <line x1={fromPos.x} y1={fromPos.y - 16.5} x2={fromPos.x} y2={fromPos.y - 10.5} stroke="rgba(255, 251, 235, 0.9)" strokeWidth="1.6" strokeLinecap="round" className="mark-last-tick" />
            <line x1={fromPos.x} y1={fromPos.y + 10.5} x2={fromPos.x} y2={fromPos.y + 16.5} stroke="rgba(255, 251, 235, 0.9)" strokeWidth="1.6" strokeLinecap="round" className="mark-last-tick" />

            {/* Destination subtle aura under the piece */}
            <circle cx={toPos.x} cy={toPos.y} r="39" fill="rgba(255, 251, 235, 0.16)" stroke="rgba(255, 251, 235, 0.45)" strokeWidth="1.4" className="mark-last-dest" />
          </g>
        );
      })()}
      {(() => {
        const isRoyalRuleset =
          state.ruleset === 'blind_si_chua' ||
          state.ruleset === 'blind_tuong_vuong' ||
          state.ruleset === 'blind_royal';
        const openingMoves = getOpeningMoves(state.ruleset, activeHandicap);
        const isHandicapPhase = openingMoves > 0 && state.ply < openingMoves;
        const handicapMovedSet =
          isHandicapPhase && state.handicap_moved_pieces && state.handicap_moved_pieces.length > 0
            ? new Set(state.handicap_moved_pieces)
            : null;

        return squares.map((piece, sq) => {
          if (!piece) return null;
          const { x, y } = pos(sq);
          const isPending = pendingMove?.to === sq;
          const chars = piece.side === 'red' ? RED_CHARS : BLACK_CHARS;
          const label = piece.covered ? '？' : piece.kind ? chars[piece.kind] : '？';
          const isHandicapMoved = handicapMovedSet ? handicapMovedSet.has(piece.id) : false;
          const royal = isRoyalRuleset ? isRoyalPiece(state.ruleset, piece, activeHandicap) : false;

          const isKychien = currentSkin === 'kychien';
          const imgSrc = getPieceAsset(
            piece.side,
            piece.kind,
            royal,
            piece.covered || !piece.kind,
            currentSkin,
          );

          return (
            <g
              key={piece.id}
              transform={`translate(${x} ${y})`}
              className={[
                'piece',
                piece.side,
                piece.covered ? 'covered' : '',
                (piece as typeof piece & { revealed_at_end?: boolean }).revealed_at_end ? 'revealed-at-end' : '',
                royal ? 'royal' : '',
                selected === sq ? 'selected' : '',
                isPending ? 'pending' : '',
                justRevealedIds.has(piece.id) ? 'revealed-pop' : '',
                isHandicapMoved ? 'handicap-moved' : '',
              ].join(' ')}
              data-square={sq}
              data-piece={piece.covered ? 'covered' : piece.kind}
              data-side={piece.side}
              onClick={() => handleTap(sq)}
            >
              {/* Hit area so clicks/taps always register across the whole piece */}
              <circle r="42" fill="transparent" style={{ pointerEvents: 'all' }} />

              {/* Keep reveal feedback on the piece itself so one move does not
                  create several competing rings and glows. */}
              {checkedGeneral === sq && <circle r="43" className="mark-check" pointerEvents="none" />}
              {selected === sq && <circle r="42" className="mark-selected" pointerEvents="none" />}
              {(piece as typeof piece & { revealed_at_end?: boolean }).revealed_at_end && (
                <circle r="40" className="mark-was-covered" pointerEvents="none" />
              )}
              {royal && <circle r="42" className="mark-royal" pointerEvents="none" />}

              {/* 3D wooden piece image */}
              <image
                href={imgSrc}
                x={isKychien ? "-44" : "-36.5"}
                y={isKychien ? "-44" : "-41.8"}
                width={isKychien ? "88" : "92"}
                height={isKychien ? "88" : "95"}
                preserveAspectRatio="xMidYMid meet"
                className="piece-img"
              />

              {justRevealedIds.has(piece.id) && <circle r="42" className="mark-reveal-ring" pointerEvents="none" />}

              {/* Hidden accessibility text */}
              <title>{label}</title>
            </g>
          );
        });
      })()}
      {[...targets].map((sq) => {
        const { x, y } = pos(sq);
        return (
          // Vùng bấm to bằng cả ô (chấm nhỏ chỉ là hình hiển thị) — bấm gần
          // đúng giao điểm là ăn, nhất là trên mobile.
          <g key={`t${sq}`} onClick={() => handleTap(sq)} style={{ cursor: 'pointer' }}>
            <circle cx={x} cy={y} r="42" fill="transparent" data-target={sq} style={{ pointerEvents: 'all' }} />
            <circle
              cx={x}
              cy={y}
              r={squares[sq] ? 44 : 12}
              className={
                squares[sq]
                  ? isPremoveTurn
                    ? 'mark-capture mark-capture-premove'
                    : 'mark-capture'
                  : isPremoveTurn
                    ? 'mark-target mark-target-premove'
                    : 'mark-target'
              }
              pointerEvents="none"
            />
          </g>
        );
      })}
      {/* Invisible tap areas for empty intersections. */}
      {squares.map((piece, sq) => {
        if (piece || targets.has(sq)) return null;
        const { x, y } = pos(sq);
        return (
          <rect
            key={`h${sq}`}
            x={x - 44}
            y={y - 44}
            width="88"
            height="88"
            fill="transparent"
            data-square={sq}
            style={{ pointerEvents: 'all' }}
            onClick={() => handleTap(sq)}
          />
        );
      })}
      {premove && (
        <g className="premove-mark" pointerEvents="none">
          <path d={`M ${pos(premove.from).x} ${pos(premove.from).y} L ${pos(premove.to).x} ${pos(premove.to).y}`} />
          <circle cx={pos(premove.from).x} cy={pos(premove.from).y} r="40" />
          <circle cx={pos(premove.to).x} cy={pos(premove.to).y} r="40" />
        </g>
      )}
      {hintMove && (
        <g className="hint-move-mark" pointerEvents="none">
          <path
            d={`M ${pos(hintMove.from).x} ${pos(hintMove.from).y} L ${pos(hintMove.to).x} ${pos(hintMove.to).y}`}
            stroke="#2ed573"
            strokeWidth="6"
            strokeDasharray="8 6"
            markerEnd="url(#arrow-hint)"
          />
          <circle cx={pos(hintMove.from).x} cy={pos(hintMove.from).y} r="38" stroke="#2ed573" strokeWidth="4" fill="none" />
          <circle cx={pos(hintMove.to).x} cy={pos(hintMove.to).y} r="38" stroke="#2ed573" strokeWidth="4" fill="rgba(46, 213, 115, 0.2)" />
        </g>
      )}

      {/* Streamer Tactical Highlights (Right-click on square) */}
      {Array.from(highlightedSquares).map((sq) => {
        const { x, y } = pos(sq);
        return (
          <g key={`sq-hl-${sq}`} className="streamer-square-highlight" pointerEvents="none">
            <circle cx={x} cy={y} r="38" fill="rgba(34, 197, 94, 0.32)" stroke="#22c55e" strokeWidth="3" />
          </g>
        );
      })}

      {/* Streamer Tactical Arrows (Right-click drag) */}
      {tacticalArrows.map((arrow, idx) => {
        const fromPos = pos(arrow.from);
        const toPos = pos(arrow.to);
        const dx = toPos.x - fromPos.x;
        const dy = toPos.y - fromPos.y;
        const len = Math.hypot(dx, dy);
        if (len === 0) return null;
        const uX = dx / len;
        const uY = dy / len;
        const startX = fromPos.x + uX * 22;
        const startY = fromPos.y + uY * 22;
        const endX = toPos.x - uX * 28;
        const endY = toPos.y - uY * 28;
        return (
          <g key={`arrow-${idx}-${arrow.from}-${arrow.to}`} className="streamer-tactical-arrow" pointerEvents="none">
            <line
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke="#22c55e"
              strokeWidth="7"
              strokeLinecap="round"
              opacity="0.9"
              markerEnd="url(#arrow-tactical)"
            />
          </g>
        );
      })}
    </svg>
  );
}

function BoardLines() {
  const x = (f: number): number => MARGIN + f * CELL;
  const y = (r: number): number => MARGIN + r * CELL;
  const lines: string[] = [];
  for (let r = 0; r < 10; r += 1) lines.push(`M ${x(0)} ${y(r)} H ${x(8)}`);
  // Vertical lines break at the river (screen rows 4-5) except the edges.
  for (let f = 0; f < 9; f += 1) {
    if (f === 0 || f === 8) {
      lines.push(`M ${x(f)} ${y(0)} V ${y(9)}`);
    } else {
      lines.push(`M ${x(f)} ${y(0)} V ${y(4)}`, `M ${x(f)} ${y(5)} V ${y(9)}`);
    }
  }
  // Palace diagonals (screen rows 0-2 and 7-9, files 3-5).
  lines.push(
    `M ${x(3)} ${y(0)} L ${x(5)} ${y(2)}`,
    `M ${x(5)} ${y(0)} L ${x(3)} ${y(2)}`,
    `M ${x(3)} ${y(7)} L ${x(5)} ${y(9)}`,
    `M ${x(5)} ${y(7)} L ${x(3)} ${y(9)}`,
  );
  return (
    <g className="grid" pointerEvents="none">
      <path d={lines.join(' ')} />
      <text x={x(4)} y={y(4) + 58} textAnchor="middle" className="river-text">
        {'楚 河      漢 界'}
      </text>
    </g>
  );
}
